import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  ScanSearch, Upload, Loader2, CheckCircle2, AlertTriangle, Ban,
  X, ChevronLeft, ChevronRight, Trash2, AlertCircle, ListChecks,
  Briefcase, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { Btn } from "@/components/ui-kit";
import { formatDateUK } from "@/lib/format";
import api from "@/lib/api";

// ──────────────────────────────────────────────────────────────────────────
// Visual styling for findings & verdicts
// ──────────────────────────────────────────────────────────────────────────

const SEVERITY_STYLES = {
  Critical: { dot: "bg-red-500", chip: "bg-red-100 text-red-800 border-red-300", strip: "border-l-red-500" },
  Warning: { dot: "bg-amber-500", chip: "bg-amber-100 text-amber-800 border-amber-300", strip: "border-l-amber-500" },
  Note: { dot: "bg-sky-500", chip: "bg-sky-100 text-sky-800 border-sky-300", strip: "border-l-sky-500" },
};

const VERDICT_STYLES = {
  no_issues: { Icon: CheckCircle2, bg: "bg-emerald-50 border-emerald-300", text: "text-emerald-900", iconBg: "bg-emerald-500" },
  manual_review: { Icon: AlertTriangle, bg: "bg-amber-50 border-amber-300", text: "text-amber-900", iconBg: "bg-amber-500" },
  do_not_print: { Icon: Ban, bg: "bg-red-50 border-red-300", text: "text-red-900", iconBg: "bg-red-500" },
};

// ──────────────────────────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────────────────────────

const FindingCard = ({ finding, index }) => {
  const s = SEVERITY_STYLES[finding.severity] || SEVERITY_STYLES.Note;
  return (
    <div
      className={`bg-white border border-zinc-200 border-l-4 ${s.strip} rounded-lg p-3 shadow-sm`}
      data-testid={`vc-finding-${finding.severity.toLowerCase()}-${index}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <p className="font-semibold text-zinc-950 text-sm leading-snug">{finding.summary}</p>
        <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${s.chip} shrink-0`}>{finding.severity}</span>
      </div>
      {finding.explanation && <p className="text-xs text-zinc-700 leading-relaxed">{finding.explanation}</p>}
      {finding.action && (
        <p className="text-xs text-zinc-900 mt-1.5">
          <span className="font-semibold">Action: </span>
          <span>{finding.action}</span>
        </p>
      )}
    </div>
  );
};

const VerdictBanner = ({ verdict }) => {
  if (!verdict) return null;
  // "Couldn't run" gets the danger red palette since something needs the
  // user's attention (likely OpenAI billing). Falls back to the standard
  // verdict palette for the normal cases.
  const palette = verdict.all_errored ? VERDICT_STYLES.do_not_print : VERDICT_STYLES[verdict.verdict];
  const v = palette || VERDICT_STYLES.manual_review;
  const Icon = verdict.all_errored ? Ban : v.Icon;
  const c = verdict.counts || {};
  const total = (c.Critical || 0) + (c.Warning || 0) + (c.Note || 0);
  return (
    <div className={`rounded-xl border-2 p-3.5 flex items-start gap-3 ${v.bg}`} data-testid="vc-verdict">
      <span className={`grid place-items-center w-10 h-10 rounded-full text-white shadow ${v.iconBg} shrink-0`}>
        <Icon className="w-5 h-5" />
      </span>
      <div className="flex-1 min-w-0">
        <p className={`font-display text-base font-bold ${v.text}`}>{verdict.label}</p>
        <p className={`text-[11px] mt-0.5 ${v.text} opacity-80`}>
          {verdict.all_errored
            ? "OpenAI couldn't process any of the pages — see the per-page error below. Most often this is an OpenAI billing/quota issue: top up at platform.openai.com/account/billing, then re-run."
            : total === 0
            ? "AI didn't surface any visual concerns. A human eyeball pass is still recommended for high-value print runs."
            : (
              <>
                {c.Critical > 0 && <span className="mr-2"><strong>{c.Critical}</strong> critical</span>}
                {c.Warning > 0 && <span className="mr-2"><strong>{c.Warning}</strong> warning{c.Warning === 1 ? "" : "s"}</span>}
                {c.Note > 0 && <span className="mr-2"><strong>{c.Note}</strong> note{c.Note === 1 ? "" : "s"}</span>}
                {verdict.has_errors && <span className="opacity-75">· {verdict.pages_with_errors || ""} page{(verdict.pages_with_errors || 0) === 1 ? "" : "s"} failed</span>}
              </>
            )}
        </p>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Upload form — the first thing the user sees when the dialog opens
// ──────────────────────────────────────────────────────────────────────────

const UploadForm = ({ keyStatus, projects, onSubmit, busy }) => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [checkSpelling, setCheckSpelling] = useState(false);
  const [saveAgainst, setSaveAgainst] = useState(false);
  const [projectId, setProjectId] = useState("");

  const submit = () => {
    if (!file) {
      toast.error("Pick a PDF first.");
      return;
    }
    onSubmit({
      file,
      checkSpelling,
      projectId: saveAgainst ? projectId || null : null,
    });
  };

  const sortedProjects = useMemo(() => {
    return [...(projects || [])].sort((a, b) => (b.job_number || 0) - (a.job_number || 0));
  }, [projects]);

  return (
    <div className="space-y-4 max-w-2xl mx-auto" data-testid="vc-upload-form">
      <div className="text-center space-y-1">
        <div className="inline-grid place-items-center w-12 h-12 rounded-full bg-zinc-900 text-white mb-2">
          <ScanSearch className="w-6 h-6" />
        </div>
        <h2 className="font-display text-2xl font-bold text-zinc-950">Artwork Visual Check</h2>
        <p className="text-sm text-zinc-600 max-w-md mx-auto">
          Upload your final artwork PDF. AI scans every page for obvious print glitches — fuzzy images, broken cut-outs, missing logos, bleed concerns. Not a full preflight.
        </p>
      </div>

      {!keyStatus?.configured && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm text-amber-900" data-testid="vc-no-key-warning">
          <p className="font-semibold inline-flex items-center gap-1.5"><AlertCircle className="w-4 h-4" />OpenAI API key not set</p>
          <p className="text-xs mt-1 text-amber-800">Add one in <strong>Admin → Visual Check settings</strong> before running a check.</p>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,.pdf"
        hidden
        onChange={(e) => setFile(e.target.files?.[0] || null)}
        data-testid="vc-file-input"
      />

      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className={`w-full rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors ${file ? "border-emerald-400 bg-emerald-50" : "border-zinc-300 bg-zinc-50 hover:border-zinc-500"}`}
        data-testid="vc-file-picker"
      >
        {file ? (
          <>
            <CheckCircle2 className="w-7 h-7 text-emerald-600 mx-auto mb-1.5" />
            <p className="font-semibold text-emerald-900 text-sm">{file.name}</p>
            <p className="text-[11px] text-emerald-700/80 mt-0.5">{Math.round(file.size / 1024)} KB · click to change</p>
          </>
        ) : (
          <>
            <Upload className="w-7 h-7 text-zinc-500 mx-auto mb-1.5" />
            <p className="font-semibold text-zinc-800 text-sm">Click to pick a PDF</p>
            <p className="text-[11px] text-zinc-500 mt-0.5">Up to 60 pages · rendered at 200 DPI</p>
          </>
        )}
      </button>

      <div className="space-y-2.5">
        <label className="flex items-center gap-2.5 cursor-pointer select-none p-2.5 rounded-lg hover:bg-zinc-50">
          <input
            type="checkbox"
            checked={checkSpelling}
            onChange={(e) => setCheckSpelling(e.target.checked)}
            disabled={busy}
            className="w-4 h-4 shrink-0"
            data-testid="vc-spelling-toggle"
          />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-zinc-900">Also check spelling</p>
            <p className="text-[11px] text-zinc-500">Brand names &amp; stylisations are explicitly ignored.</p>
          </div>
        </label>

        <label className="flex items-start gap-2.5 cursor-pointer select-none p-2.5 rounded-lg hover:bg-zinc-50">
          <input
            type="checkbox"
            checked={saveAgainst}
            onChange={(e) => setSaveAgainst(e.target.checked)}
            disabled={busy}
            className="w-4 h-4 shrink-0 mt-0.5"
            data-testid="vc-save-toggle"
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-zinc-900 inline-flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5" />Save against a project</p>
            <p className="text-[11px] text-zinc-500">Default is ad-hoc — nothing is saved. Tick to keep a record under one of your jobs.</p>
            {saveAgainst && (
              <select
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                disabled={busy}
                className="mt-2 w-full text-sm border border-zinc-300 rounded-md px-2.5 py-1.5"
                data-testid="vc-project-select"
              >
                <option value="">— pick a project —</option>
                {sortedProjects.map((p) => (
                  <option key={p.id} value={p.id}>#{p.job_number} · {p.name} · {p.client_name}</option>
                ))}
              </select>
            )}
          </div>
        </label>
      </div>

      <Btn
        type="button"
        onClick={submit}
        disabled={busy || !file || !keyStatus?.configured || (saveAgainst && !projectId)}
        className="w-full justify-center !py-3"
        data-testid="vc-run-button"
      >
        {busy ? <><Loader2 className="w-4 h-4 animate-spin" />Checking — this can take 1–2 min for long PDFs…</> : <><Sparkles className="w-4 h-4" />Run visual check</>}
      </Btn>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Report viewer — proofing-style page-by-page with sidebar findings
// ──────────────────────────────────────────────────────────────────────────

const ReportViewer = ({ report, onReset, onDelete }) => {
  const pages = report.pages || [];
  const totalFindings = pages.reduce((acc, p) => acc + (p.findings?.length || 0), 0);
  const [activePage, setActivePage] = useState(1);

  // Fetch each rendered page via authenticated axios + blob URL. We can't
  // rely on the browser sending the HttpOnly auth cookie on a bare <img>
  // request — Safari's ITP and a few cross-origin configs silently strip it,
  // leaving a broken-image placeholder. Fetching with axios reuses the same
  // auth headers (Bearer + cookie) the rest of the app uses, then we hand
  // the blob to <img> via a local object URL.
  const [imgUrl, setImgUrl] = useState(null);
  const [imgError, setImgError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    setImgError(null);
    setImgUrl(null);
    api
      .get(`/visual-check/${report.id}/pages/${activePage}`, { responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        createdUrl = URL.createObjectURL(res.data);
        setImgUrl(createdUrl);
      })
      .catch((err) => {
        if (!cancelled) setImgError(err?.response?.statusText || err?.message || "Couldn't load page image.");
      });
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [report.id, activePage]);

  const cur = pages.find((p) => p.page_number === activePage);

  // Build an ordered queue of (page, index-within-page) for every finding so
  // the "Jump to next finding" button can step through the whole report.
  const findingQueue = useMemo(() => {
    const queue = [];
    pages.forEach((p) => {
      (p.findings || []).forEach((_, idx) => queue.push({ page: p.page_number, idx }));
    });
    return queue;
  }, [pages]);
  const [cursor, setCursor] = useState(-1);

  const jumpNext = () => {
    if (findingQueue.length === 0) return;
    const next = (cursor + 1) % findingQueue.length;
    setCursor(next);
    setActivePage(findingQueue[next].page);
  };

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="vc-report-viewer">
      {/* Top bar: meta + delete */}
      <div className="px-5 py-3 border-b border-zinc-200 bg-white flex items-center justify-between gap-3 flex-wrap shrink-0">
        <div className="min-w-0">
          <p className="font-display text-base font-bold text-zinc-950 truncate" data-testid="vc-report-filename">{report.file_name}</p>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            {report.page_count} pages · {totalFindings} finding{totalFindings === 1 ? "" : "s"} · spelling check {report.check_spelling ? "on" : "off"} · {formatDateUK(report.ran_at)}
            {report.project_name && <> · saved to <strong>#{report.job_number} {report.project_name}</strong></>}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={onReset}
            className="text-xs font-medium px-3 py-1.5 rounded-full bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
            data-testid="vc-back-to-upload"
          >
            <Upload className="w-3 h-3 inline mr-1" />Check another PDF
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="text-xs font-medium text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-full inline-flex items-center gap-1"
            data-testid="vc-delete-report"
          >
            <Trash2 className="w-3 h-3" />Delete
          </button>
        </div>
      </div>

      {/* Verdict + jump-next */}
      <div className="px-5 py-3 bg-zinc-50 border-b border-zinc-200 space-y-3 shrink-0">
        <VerdictBanner verdict={report.verdict} />
        {totalFindings > 0 && (
          <button
            type="button"
            onClick={jumpNext}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-amber-600 text-white hover:bg-amber-700 shadow-sm"
            data-testid="vc-jump-next"
          >
            <ChevronRight className="w-4 h-4" />Jump to next finding
          </button>
        )}
      </div>

      {/* Two-column body */}
      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[1fr,460px] gap-0 overflow-hidden">
        {/* Page preview */}
        <div className="bg-zinc-100 border-r border-zinc-200 flex flex-col min-h-0">
          <div className="px-4 py-2 border-b border-zinc-200 bg-white flex items-center justify-between gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setActivePage((p) => Math.max(1, p - 1))}
              disabled={activePage <= 1}
              className="p-1.5 rounded hover:bg-zinc-100 disabled:opacity-40"
              data-testid="vc-prev-page"
            ><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-xs text-zinc-700 font-medium tabular-nums">Page {activePage} / {report.page_count}</span>
            <button
              type="button"
              onClick={() => setActivePage((p) => Math.min(report.page_count, p + 1))}
              disabled={activePage >= report.page_count}
              className="p-1.5 rounded hover:bg-zinc-100 disabled:opacity-40"
              data-testid="vc-next-page"
            ><ChevronRight className="w-4 h-4" /></button>
          </div>
          <div className="flex-1 overflow-auto p-4 flex items-start justify-center">
            {imgError ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-900 max-w-md text-center">
                <AlertCircle className="w-6 h-6 mx-auto mb-2" />
                <p className="font-semibold">Couldn&apos;t load page preview</p>
                <p className="text-xs mt-1 text-red-800">{imgError}</p>
              </div>
            ) : !imgUrl ? (
              <div className="grid place-items-center w-full py-20 text-zinc-500 text-sm">
                <Loader2 className="w-5 h-5 animate-spin mb-2" />
                Loading page {activePage}…
              </div>
            ) : (
              <img
                src={imgUrl}
                alt={`Page ${activePage}`}
                className="max-w-full h-auto rounded shadow-md border border-zinc-300 bg-white"
                data-testid={`vc-page-image-${activePage}`}
              />
            )}
          </div>
        </div>

        {/* Sidebar: findings per page + thumbnails */}
        <div className="flex flex-col min-h-0 bg-white">
          <div className="px-4 py-3 border-b border-zinc-200 shrink-0">
            <h4 className="font-display font-bold text-zinc-950 flex items-center gap-2">
              <ListChecks className="w-4 h-4" />
              Findings on page {activePage}
            </h4>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              {cur?.findings?.length || 0} finding{(cur?.findings?.length || 0) === 1 ? "" : "s"} on this page
              · {totalFindings} total
            </p>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2.5" data-testid="vc-findings-list">
            {!cur && <p className="text-sm text-zinc-500 italic">No data for this page.</p>}
            {cur?.error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-900">
                <p className="font-semibold mb-1">Couldn&apos;t check this page</p>
                <p className="text-xs text-red-800">{cur.error}</p>
              </div>
            )}
            {cur && !cur.error && (cur.findings?.length || 0) === 0 && (
              <p className="text-sm text-emerald-700 inline-flex items-center gap-1.5 italic">
                <CheckCircle2 className="w-4 h-4" />No obvious issues on this page.
              </p>
            )}
            {(cur?.findings || []).map((f, i) => <FindingCard key={i} finding={f} index={i} />)}
          </div>
          {/* Thumb strip */}
          <div className="border-t border-zinc-200 bg-zinc-50 px-3 py-2 overflow-x-auto shrink-0">
            <div className="flex gap-1.5">
              {pages.map((p) => {
                const cnt = (p.findings || []).length;
                const hasCritical = (p.findings || []).some((f) => f.severity === "Critical");
                const tone = hasCritical ? "bg-red-500" : cnt > 0 ? "bg-amber-500" : "bg-emerald-500";
                return (
                  <button
                    key={p.page_number}
                    type="button"
                    onClick={() => setActivePage(p.page_number)}
                    className={`shrink-0 w-9 h-12 rounded border-2 text-[10px] font-bold flex flex-col items-center justify-center gap-0.5 transition-colors ${p.page_number === activePage ? "border-zinc-900 bg-white" : "border-zinc-300 bg-white hover:border-zinc-500"}`}
                    title={`Page ${p.page_number} — ${cnt} finding${cnt === 1 ? "" : "s"}`}
                    data-testid={`vc-thumb-${p.page_number}`}
                  >
                    <span className="text-zinc-700">{p.page_number}</span>
                    <span className={`w-2 h-2 rounded-full ${tone}`} />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Main dialog
// ──────────────────────────────────────────────────────────────────────────

const VisualCheckDialog = ({ open, onClose }) => {
  const [keyStatus, setKeyStatus] = useState(null);
  const [projects, setProjects] = useState([]);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  // Reload key status + projects on open so a fresh open picks up any new key.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const [k, p] = await Promise.all([
          api.get("/admin/settings/openai-key"),
          api.get("/projects"),
        ]);
        if (!cancelled) {
          setKeyStatus(k.data);
          setProjects(Array.isArray(p.data) ? p.data : (p.data?.items || []));
        }
      } catch (err) {
        if (!cancelled) {
          setKeyStatus({ configured: false });
          setProjects([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const handleRun = async ({ file, checkSpelling, projectId }) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("check_spelling", checkSpelling ? "true" : "false");
    if (projectId) fd.append("project_id", projectId);
    setBusy(true);
    try {
      toast.info("Rendering pages and asking OpenAI — this can take 1–2 min for long PDFs…");
      const { data } = await api.post("/visual-check", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000,
      });
      setReport(data);
      toast.success("Check complete", { description: data.verdict?.label });
    } catch (err) {
      toast.error("Check failed", { description: err?.response?.data?.detail || err?.message });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!report) return;
    if (!window.confirm("Delete this visual-check report? The rendered pages will be removed too.")) return;
    try {
      await api.delete(`/visual-check/${report.id}`);
      toast.success("Report deleted.");
      setReport(null);
    } catch (err) {
      toast.error("Couldn't delete report", { description: err?.response?.data?.detail || err?.message });
    }
  };

  const handleClose = () => {
    setReport(null);
    onClose?.();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center" data-testid="vc-dialog">
      <button
        type="button"
        aria-label="Close"
        onClick={handleClose}
        className="absolute inset-0 bg-zinc-950/55 backdrop-blur-sm"
        data-testid="vc-dialog-backdrop"
      />
      <div className="relative bg-white shadow-2xl flex flex-col w-full h-full sm:my-4 sm:mx-4 sm:max-h-[calc(100vh-2rem)] sm:max-w-6xl sm:rounded-2xl overflow-hidden">
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-3 right-3 z-10 grid place-items-center w-9 h-9 rounded-full bg-white border border-zinc-200 hover:bg-zinc-100 shadow-sm"
          title="Close"
          data-testid="vc-dialog-close"
        >
          <X className="w-4 h-4" />
        </button>
        {!report ? (
          <div className="flex-1 overflow-y-auto p-6 sm:p-10 flex items-center">
            <div className="w-full">
              <UploadForm keyStatus={keyStatus} projects={projects} onSubmit={handleRun} busy={busy} />
            </div>
          </div>
        ) : (
          <ReportViewer report={report} onReset={() => setReport(null)} onDelete={handleDelete} />
        )}
      </div>
    </div>
  );
};

export default VisualCheckDialog;
